




























































































































































































import { QueryBuilder } from '@/app/components';
import { ExclamationCircleIcon } from '@vue-hero-icons/outline';
import { PropType, computed, defineComponent, ref, watch } from '@vue/composition-api';
import * as R from 'ramda';
import { useCleaningConstraints } from '../../../composable/cleaning-constraints';
import { HarvesterBlockId } from '../../../constants';
import { CleaningConfiguration, CleaningFieldConfiguration, Condition, Constraint } from '../../../types/cleaning.type';
import SampleValues from '../../SampleValues.vue';
import OutliersRule from '../OutliersRule.vue';
import EditConstraint from './EditConstraint.vue';
import ViewConstraint from './ViewConstraint.vue';
import ConstraintErrors from './ConstraintErrors.vue';
import { useSampleFields, useCleaning } from '../../../composable';

export default defineComponent({
    name: 'EditAdvancedContstraint',
    components: {
        QueryBuilder,
        EditConstraint,
        ViewConstraint,
        OutliersRule,
        SampleValues,
        ExclamationCircleIcon,
        ConstraintErrors,
    },
    props: {
        configuration: {
            type: Object as PropType<CleaningConfiguration>,
            required: true,
        },
        constraint: {
            type: Object as PropType<Constraint>,
            required: true,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        harvesterBlockId: {
            type: String as PropType<HarvesterBlockId>,
            required: true,
        },
        outlierFields: {
            type: Array as PropType<CleaningFieldConfiguration[]>,
            required: true,
        },
        selectedFieldsTypes: {
            type: Array as PropType<string[]>,
            required: true,
        },
        previousProcessedSample: {
            type: Array,
            default: null,
        },
        sample: {
            type: Array,
            required: true,
        },
        multipleOutlierFields: {
            type: Boolean,
            default: false,
        },
        canRunManyTimes: {
            type: Boolean,
            default: false,
        },
        allowDropOrDefaultOnly: {
            type: Boolean,
            default: false,
        },
    },
    setup(props, { emit }) {
        const { extractFieldSample } = useSampleFields();

        const editingConstraint = ref<boolean>(false);
        const editableConstraint = ref<Constraint>(props.constraint);
        const selectedFields = ref<any[]>([]);
        const isNew = ref(editableConstraint.value.structure?.conditions.length === 0);
        const sameConditionsInGroup = ref<boolean>(false);
        const emptyGroups = ref<boolean>(false);
        const multipleSelectedField = ref<boolean>(false);
        const typesRef = computed(() => props.selectedFieldsTypes);

        const {
            selectedFieldsTypes: involvedFieldsTypes,
            availableConstraintOptions,
            alreadyDefinedConstraintTypes,
            referencedFields,
        } = useCleaningConstraints(props.configuration, selectedFields);

        const canCreateConstraint = computed(() => {
            if (editingConstraint.value || sameConditionsInGroup.value || emptyGroups.value) return false;
            if (
                !editableConstraint.value.structure?.conditions?.length ||
                !editableConstraint.value?.outliersRule?.type
            )
                return false;
            if (editableConstraint.value.outliersRule.type) {
                if (
                    editableConstraint.value.outliersRule.type === 'DEFAULT_VALUE' &&
                    (editableConstraint.value.outliersRule.replaceValue === null ||
                        editableConstraint.value.outliersRule.replaceValue === '')
                ) {
                    return false;
                }
                return !(
                    editableConstraint.value.outliersRule.type === 'PREVIOUS_VALUE' &&
                    (editableConstraint.value.outliersRule.secondaryRule?.replaceValue === null ||
                        editableConstraint.value.outliersRule.secondaryRule?.replaceValue === '')
                );
            }
            return true;
        });

        const newConditionTemplate = ref({
            fieldName: null,
            multipleOperator: 'none',
            type: null,
            details: null,
        });

        const newGroupTemplate = ref({
            operator: 'AND',
            conditions: [],
        });

        const themeCalculator = (query: any) => {
            switch (query.operator) {
                case 'AND':
                    return 'purple';
                case 'OR':
                    return 'teal';
                default:
                    return null;
            }
        };

        // Check if the selected fields have inconsistent lengths
        const inconsistentMultipleFields = computed(() => {
            if (!selectedFields.value.length || !multipleSelectedField.value) return false;
            const lengths: number[] = [];
            for (const field of props.outlierFields.concat(selectedFields.value)) {
                const fieldSample = extractFieldSample(props.sample, field.title, field.path);
                if (!fieldSample[0] || !R.is(Array, fieldSample[0])) return false;
                lengths.push(fieldSample[0].length);
            }
            if (lengths.length === 0) return false;
            return lengths.some((length) => length !== lengths[0]);
        });

        const multipleOperatorOptions = computed(() => [
            {
                value: 'element',
                title: 'Same element position',
                description:
                    'The values of the specific field that are in the same array position (element) in each record need to satisfy the quality rule',
                available: props.multipleOutlierFields && !inconsistentMultipleFields.value,
            },
            {
                value: 'all',
                title: 'All array values',
                description:
                    'All values of the specific field (across all array elements) in each record need to satisfy the quality rule',
                available: true,
            },
            {
                value: 'any',
                title: 'Any array value',
                description:
                    'A value of the specific field in any array position (element) in each record should satisfy the quality rule',
                available: true,
            },
        ]);

        const hasConditions = computed(() => editableConstraint.value.structure?.conditions?.length);

        const fieldChanged = (fieldName: string, query?: any) => {
            if (query) {
                query.details = null;
                query.type = null;
                query.multipleOperator = 'none';
                query.parentFieldName = (props.constraint?.fieldName as string)?.replaceAll('__', '.');
                query.fieldType = getFieldType(fieldName)[0];
            }
            selectedFields.value = [];
            multipleSelectedField.value = false;
            const foundField = props.configuration.fields.find((f: any) => f.name === fieldName);
            if (foundField) {
                selectedFields.value = [foundField];
                multipleSelectedField.value =
                    !!foundField.multiple || foundField.path.some((pathPart: string) => pathPart.endsWith('[]'));
            }
        };

        const { setDateTime, parseTimeStringToDate } = useCleaning(typesRef);

        const saveConstraint = () => {
            if (
                (props.selectedFieldsTypes.includes('datetime') ||
                    props.selectedFieldsTypes.includes('date') ||
                    props.selectedFieldsTypes.includes('time')) &&
                editableConstraint.value.outliersRule?.replaceValue
            ) {
                editableConstraint.value.outliersRule.replaceValue = setDateTime(
                    editableConstraint.value.outliersRule.replaceValue as string,
                );
            }

            if (isNew.value) {
                emit('save-create');
            } else {
                emit('save-update');
            }
        };

        const checkGroup = (conditions: Condition[]) => {
            const nonAdvancedConditions = conditions
                .filter((c) => c.operator === undefined)
                .map((c) => {
                    const result = R.clone(c);
                    delete result.conditionUid;
                    return result;
                })
                .map((c) => JSON.stringify(c));
            if (nonAdvancedConditions.length !== new Set(nonAdvancedConditions).size) {
                sameConditionsInGroup.value = true;
            }
            conditions.forEach((c) => {
                if (c.conditions !== undefined) {
                    if (c.conditions.length > 0) checkGroup(c.conditions);
                    else emptyGroups.value = true;
                }
            });
        };

        const handleEmptyConditions = (isRoot: boolean) => {
            if (isRoot) emit('delete-constraint');
        };

        const getFieldType = (fieldName: string) => {
            if (!fieldName) return [];
            const field = props.configuration.fields.find((f: any) => f.name === fieldName);
            return field ? [field.type] : [];
        };

        const decimalPlacesError = computed(() => {
            const errorsArray: string[] = [];

            if (props.selectedFieldsTypes.includes('double') || props.selectedFieldsTypes.includes('float')) {
                const fieldsToCheck = [
                    editableConstraint.value.outliersRule?.replaceValue,
                    editableConstraint.value.outliersRule?.secondaryRule?.replaceValue,
                ];

                // current precision is 7 decimal places
                const precision = Math.pow(10, 7);

                const hasNoMoreThan7DecimalPlaces = (value: string) => {
                    return Math.round(Number(value) * precision) / precision === Number(value);
                };

                fieldsToCheck.forEach((field) => {
                    if (field && !hasNoMoreThan7DecimalPlaces(field as string)) {
                        errorsArray.push(
                            `Invalid number <span class="font-bold">"${field}"</span>. Must have no more than 7 decimal places.`,
                        );
                    }
                });
            }
            return R.uniq(errorsArray);
        });

        const integerError = computed(() => {
            const errorsArray: string[] = [];

            if (props.selectedFieldsTypes.includes('integer')) {
                const fieldsToCheck = [
                    editableConstraint.value.outliersRule?.replaceValue,
                    editableConstraint.value.outliersRule?.secondaryRule?.replaceValue,
                ];

                fieldsToCheck.forEach((field) => {
                    if (field && !Number.isInteger(Number(field))) {
                        errorsArray.push(
                            `Invalid number <span class="font-bold">"${field}"</span>. Must be an integer.`,
                        );
                    }
                });
            }
            return R.uniq(errorsArray);
        });

        const errors = computed(() => {
            return [...decimalPlacesError.value, ...integerError.value];
        });

        if (!isNew.value) {
            const { outliersRule } = editableConstraint.value;

            if (props.selectedFieldsTypes.includes('time')) {
                if (outliersRule?.replaceValue) {
                    outliersRule.replaceValue = parseTimeStringToDate(outliersRule.replaceValue as string);
                } else if (outliersRule?.secondaryRule?.replaceValue) {
                    outliersRule.secondaryRule.replaceValue = parseTimeStringToDate(
                        outliersRule.secondaryRule.replaceValue as string,
                    );
                }
            }
        }

        watch(editableConstraint.value, (constraint: Constraint) => {
            if (constraint.structure?.conditions?.length) {
                sameConditionsInGroup.value = false;
                emptyGroups.value = false;
                checkGroup(constraint.structure.conditions);
            }
        });

        return {
            isNew,
            editingConstraint,
            editableConstraint,
            newConditionTemplate,
            newGroupTemplate,
            themeCalculator,
            alreadyDefinedConstraintTypes,
            availableConstraintOptions,
            referencedFields,
            hasConditions,
            canCreateConstraint,
            selectedFields,
            fieldChanged,
            involvedFieldsTypes,
            saveConstraint,
            emptyGroups,
            sameConditionsInGroup,
            handleEmptyConditions,
            getFieldType,
            multipleSelectedField,
            multipleOperatorOptions,
            errors,
            inconsistentMultipleFields,
        };
    },
});
